import React, { useRef, useState, useEffect } from 'react';
import LinkModal from '../../ui/LinkModal';

const TextProperties = ({ block, onUpdate }) => {
    const fonts = ['Lato', 'Roboto', 'Montserrat', 'Open Sans', 'Poppins'];
    const editorRef = useRef(null);
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
    const savedSelection = useRef(null);

    useEffect(() => {
        const editor = editorRef.current;
        if (editor && editor.innerHTML !== block.content) {
            editor.innerHTML = block.content;
        }
    }, [block.content]);

    const handleContentChange = (e) => {
        onUpdate(block.id, { content: e.currentTarget.innerHTML });
    };
    
    const applyStyle = (command, value = null) => {
        document.execCommand(command, false, value);
        if (editorRef.current) {
            onUpdate(block.id, { content: editorRef.current.innerHTML });
        }
    };
    
    const saveSelection = () => {
        if (window.getSelection) {
            const sel = window.getSelection();
            if (sel.getRangeAt && sel.rangeCount) {
                savedSelection.current = sel.getRangeAt(0);
            }
        }
    };

    const restoreSelection = () => {
        if (savedSelection.current) {
            if (window.getSelection) {
                const sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(savedSelection.current);
            }
        }
    };
    
    const handleLinkClick = () => {
        saveSelection();
        setIsLinkModalOpen(true);
    };

    const handleSetLink = (url) => {
        restoreSelection();
        if (editorRef.current) {
            editorRef.current.focus();
            applyStyle('createLink', url);
        }
        setIsLinkModalOpen(false);
    };

    return (
        <>
            <LinkModal isOpen={isLinkModalOpen} onClose={() => setIsLinkModalOpen(false)} onConfirm={handleSetLink} />
            <div className="space-y-4">
                {block.type === 'text' ? (
                    <div>
                        <label className="block text-sm font-medium">Contenu</label>
                        <div 
                            ref={editorRef}
                            contentEditable
                            onBlur={handleContentChange}
                            dangerouslySetInnerHTML={{ __html: block.content }}
                            className="rich-text-editor w-full p-2 border rounded bg-white min-h-[100px]"
                        />
                    </div>
                ) : (
                     <div>
                        <label className="block text-sm font-medium">Texte</label>
                        <input type="text" value={block.content} onChange={(e) => onUpdate(block.id, { content: e.target.value })} className="w-full p-2 border rounded" />
                    </div>
                )}
                
                {block.type === 'text' && (
                     <div>
                        <label className="block text-sm font-medium">Édition du texte sélectionné</label>
                         <div className="flex items-center gap-1 mt-1 rounded-md border p-1 flex-wrap">
                            <button onClick={() => applyStyle('bold')} className="p-2 rounded bg-white hover:bg-gray-100"><span className="font-bold">G</span></button>
                            <button onClick={() => applyStyle('italic')} className="p-2 rounded bg-white hover:bg-gray-100"><span className="italic">I</span></button>
                            <button onClick={() => applyStyle('underline')} className="p-2 rounded bg-white hover:bg-gray-100"><span className="underline">S</span></button>
                            <button onClick={handleLinkClick} className="p-2 rounded bg-white hover:bg-gray-100">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path></svg>
                            </button>
                            <input type="color" onChange={(e) => applyStyle('foreColor', e.target.value)} className="w-8 h-8" />
                        </div>
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium">Police (bloc entier)</label>
                    <select value={block.fontFamily} onChange={(e) => onUpdate(block.id, { fontFamily: e.target.value })} className="w-full p-2 border rounded">
                        {fonts.map(font => <option key={font} value={font}>{font}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium">Taille ({block.fontSize}px)</label>
                    <input type="range" min="12" max="48" value={block.fontSize} onChange={(e) => onUpdate(block.id, { fontSize: parseInt(e.target.value) })} className="w-full" />
                </div>
                 {block.type === 'title' && (
                     <div>
                        <label className="block text-sm font-medium">Couleur du texte</label>
                        <input type="color" value={block.color} onChange={(e) => onUpdate(block.id, { color: e.target.value })} className="w-full h-8" />
                    </div>
                )}
                <div className="space-y-2">
                    <div>
                        <label className="block text-sm font-medium">Alignement</label>
                        <div className="flex items-center gap-1 mt-1 rounded-md border p-1">
                            <button onClick={() => onUpdate(block.id, { textAlign: 'left' })} className={`p-2 w-full rounded ${block.textAlign === 'left' ? 'bg-blue-500 text-white' : 'bg-white hover:bg-gray-100'}`}><svg className="w-5 h-5 mx-auto" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"></path></svg></button>
                            <button onClick={() => onUpdate(block.id, { textAlign: 'center' })} className={`p-2 w-full rounded ${block.textAlign === 'center' ? 'bg-blue-500 text-white' : 'bg-white hover:bg-gray-100'}`}><svg className="w-5 h-5 mx-auto" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"></path></svg></button>
                            <button onClick={() => onUpdate(block.id, { textAlign: 'right' })} className={`p-2 w-full rounded ${block.textAlign === 'right' ? 'bg-blue-500 text-white' : 'bg-white hover:bg-gray-100'}`}><svg className="w-5 h-5 mx-auto" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM9 10a1 1 0 011-1h6a1 1 0 110 2h-6a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"></path></svg></button>
                        </div>
                    </div>
                </div>
                <div className="pt-4 border-t">
                    <label className="block text-sm font-medium">Marge haute ({block.paddingTop || 0}px)</label>
                    <input type="range" min="0" max="64" value={block.paddingTop || 0} onChange={(e) => onUpdate(block.id, { paddingTop: parseInt(e.target.value) })} className="w-full" />
                </div>
                <div>
                    <label className="block text-sm font-medium">Marge basse ({block.paddingBottom || 0}px)</label>
                    <input type="range" min="0" max="64" value={block.paddingBottom || 0} onChange={(e) => onUpdate(block.id, { paddingBottom: parseInt(e.target.value) })} className="w-full" />
                </div>
                 <div>
                    <label className="block text-sm font-medium">Marges latérales ({block.paddingLeft || 0}px)</label>
                    <input type="range" min="0" max="64" value={block.paddingLeft || 0} onChange={(e) => {
                        const value = parseInt(e.target.value);
                        onUpdate(block.id, { paddingLeft: value, paddingRight: value });
                    }} className="w-full" />
                </div>
            </div>
        </>
    );
};

export default TextProperties;